import { useEffect, useState } from "react";
import { Button, Card, Input, message, Space, Spin, Tooltip, Typography } from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import FlowCanvas from "../components/flows/FlowCanvas";
import { promptApi } from "../services/promptApi";
import { googleWorkspaceApi } from "../services/googleWorkspaceApi";

const { Text, Title } = Typography;
const newStep = (parentId, providers, assistants) => ({
  id: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  parentId,
  nodeType: "ai",
  name: parentId ? "Bước xử lý mới" : "Phân tích đầu vào",
  providerId: providers[0]?.id,
  assistantId: assistants[0]?.id,
  executionMode: "sync",
  prompt: "Mô tả kết quả bạn muốn AI trả về...",
});

export default function FlowEditor() {
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [providers, setProviders] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [workspaceConnections, setWorkspaceConnections] = useState([]);
  const [flow, setFlow] = useState({ name: "Flow chưa đặt tên", description: "", status: "active", steps: [] });

  useEffect(() => {
    Promise.all([promptApi.providers.list(), promptApi.assistants.list(), googleWorkspaceApi.connection(), id === "new" ? null : promptApi.flows.get(id)])
      .then(([providerItems, assistantItems, workspaceConnection, found]) => {
        setProviders(providerItems);
        setAssistants(assistantItems);
        setWorkspaceConnections(workspaceConnection?.connected ? [workspaceConnection] : []);
        setFlow(found || { name: "Flow chưa đặt tên", description: "", status: "active", steps: [newStep(null, providerItems, assistantItems)] });
      })
      .catch((error) => message.error(error.message))
      .finally(() => setLoading(false));
  }, [id]);

  const changeStep = (stepId, values) => setFlow((current) => ({ ...current, steps: current.steps.map((step) => step.id === stepId ? { ...step, ...values } : step) }));
  const addRoot = () => setFlow((current) => ({ ...current, steps: [...current.steps, newStep(null, providers, assistants)] }));
  const addNext = (stepId) => setFlow((current) => ({ ...current, steps: [...current.steps, newStep(stepId, providers, assistants)] }));
  const remove = (stepId) => setFlow((current) => {
    const removing = new Set([stepId]);
    for (const parentId of removing) current.steps.filter((step) => step.parentId === parentId).forEach((step) => removing.add(step.id));
    return { ...current, steps: current.steps.filter((step) => !removing.has(step.id)) };
  });

  const save = async () => {
    if (!flow.name.trim()) return message.warning("Flow cần có tên");
    if (!flow.steps.length) return message.warning("Flow cần có ít nhất một bước");
    if (flow.steps.some((step) => (step.nodeType || "ai") === "ai" && (!step.providerId || !step.assistantId))) return message.warning("Vui lòng chọn nhà cung cấp và trợ lý cho đầy đủ các node AI");
    setSaving(true);
    try {
      const payload = { ...flow, steps: flow.steps.map((step, index) => ({ ...step, order: step.order ?? index })) };
      id === "new" ? await promptApi.flows.create(payload) : await promptApi.flows.update(id, payload);
      message.success("Đã lưu cấu hình flow");
      history.push("/flows");
    } catch (error) { message.error(error.message); }
    finally { setSaving(false); }
  };

  if (loading) return <Card><Spin /> Đang tải cấu hình...</Card>;
  return <>
    <div className="page-heading"><Space><Tooltip title="Quay lại danh sách flow"><Button className="flow-icon-button flow-back-button" aria-label="Quay lại" icon={<ArrowLeftOutlined />} onClick={() => history.push("/flows")} /></Tooltip><div><Title level={3}>{id === "new" ? "Tạo flow mới" : "Cấu hình flow"}</Title><Text type="secondary">Màn này chỉ dùng để cấu hình cây xử lý; việc nhập/chạy kịch bản nằm ở màn Kịch bản.</Text></div></Space><Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>Lưu flow</Button></div>
    <Card className="flow-info-card"><Input className="flow-name-input" value={flow.name} onChange={(event) => setFlow({ ...flow, name: event.target.value })} /><Input value={flow.description} onChange={(event) => setFlow({ ...flow, description: event.target.value })} placeholder="Mô tả ngắn mục đích của flow" /></Card>
    <Card className="flow-editor-card"><FlowCanvas steps={flow.steps} providers={providers} assistants={assistants} workspaceConnections={workspaceConnections} editable showPrompt={false} onChange={changeStep} onDelete={remove} onAddRoot={addRoot} onAddNext={addNext} /></Card>
  </>;
}
